import { useState } from "react";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faCheck, faImages, faMagic, faUsers, faQrcode, faCircleInfo, faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { useTranslation } from "react-i18next";

const URL_META = {
  gallery: {
    icon: faImages,
    color: "#6366f1",
    bg: "#eef2ff",
  },
  magicFinder: {
    icon: faMagic,
    color: "#ec4899",
    bg: "#fdf2f8",
  },
  social: {
    icon: faUsers,
    color: "#10b981",
    bg: "#ecfdf5",
  },
};

const UrlRow = ({ urlKey, label, url, labelUrl, whatsappHref, qrUrl }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const meta = URL_META[urlKey] || { icon: faImages, color: "#6366f1", bg: "#eef2ff" };
  const description = t(`events_admin.url_desc_${urlKey}`, "");

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px 16px",
        borderRadius: "12px",
        background: "#fafafa",
        border: "1px solid #f0f0f0",
        transition: "box-shadow 0.15s",
      }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.07)")}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = "none")}
    >
      {/* Icon badge */}
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          background: meta.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <FontAwesomeIcon icon={meta.icon} style={{ color: meta.color, fontSize: "15px" }} />
      </div>

      {/* Label + URL */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "2px" }}>
          <span style={{ fontSize: "12px", fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {label}
          </span>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip style={{ maxWidth: 260 }}>
                {description}
              </Tooltip>
            }
          >
            <FontAwesomeIcon
              icon={faCircleInfo}
              style={{ color: "#9ca3af", fontSize: "11px", cursor: "pointer" }}
            />
          </OverlayTrigger>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: "12px",
            color: meta.color,
            textDecoration: "none",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
          }}
          onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")}
          onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}
        >
          {labelUrl || url}
          {!labelUrl && (
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ marginLeft: "4px", fontSize: "10px" }} />
          )}
        </a>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{copied ? "Copié !" : t("events_admin.copy")}</Tooltip>}
        >
          <button
            onClick={handleCopy}
            style={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              background: copied ? "#ecfdf5" : "#fff",
              color: copied ? "#10b981" : "#6b7280",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.15s",
              fontSize: "13px",
            }}
            onMouseEnter={e => { if (!copied) { e.currentTarget.style.background = "#f3f4f6"; e.currentTarget.style.color = "#374151"; } }}
            onMouseLeave={e => { if (!copied) { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#6b7280"; } }}
          >
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
          </button>
        </OverlayTrigger>

        {qrUrl && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{t("events_admin.download_qr")}</Tooltip>}
          >
            <a
              href={qrUrl}
              download
              style={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                background: "#fff",
                color: "#f59e0b",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.15s",
                fontSize: "14px",
                textDecoration: "none",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#fffbeb"; e.currentTarget.style.borderColor = "#fcd34d"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e5e7eb"; }}
            >
              <FontAwesomeIcon icon={faQrcode} />
            </a>
          </OverlayTrigger>
        )}
        {whatsappHref && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{t("events_admin.share_whatsapp")}</Tooltip>}
          >
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                background: "#fff",
                color: "#25D366",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.15s",
                fontSize: "14px",
                textDecoration: "none",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#f0fdf4"; e.currentTarget.style.borderColor = "#86efac"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e5e7eb"; }}
            >
              <FontAwesomeIcon icon={faWhatsapp} />
            </a>
          </OverlayTrigger>
        )}
      </div>
    </div>
  );
};

const EventUrlsCard = ({ urls }) => {
  const { t } = useTranslation();

  const buildWhatsappUrl = (galleryUrl) => {
    const text = t("events_admin.whatsapp_gallery", { url: galleryUrl });
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  const buildWhatsappMagicUrl = (aiUrl) => {
    const text = t("events_admin.whatsapp_magic", { url: aiUrl });
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  const buildWhatsappSocialUrl = (socialUrl) => {
    const text = `📸 Share your photos in our event wall! ${socialUrl}`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  if (!urls?.gallery && !urls?.magicFinder) return null;

  return (
    <Card
      className="mt-3"
      style={{
        border: "none",
        borderRadius: "16px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      <Card.Body style={{ padding: "20px 24px" }}>

        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {urls.gallery && (
            <UrlRow
              urlKey="gallery"
              label={t("events_admin.gallery")}
              url={urls.gallery}
              whatsappHref={buildWhatsappUrl(urls.gallery)}
            />
          )}
          {urls.magicFinder && (
            <UrlRow
              urlKey="magicFinder"
              label={t("events_admin.magic_finder")}
              url={urls.magicFinder}
              whatsappHref={buildWhatsappMagicUrl(urls.magicFinder)}
              qrUrl={urls.qr}
            />
          )}
          {urls.social && (
            <UrlRow
              urlKey="social"
              label="Event Wall"
              url={urls.social}
              whatsappHref={buildWhatsappSocialUrl(urls.social)}
              qrUrl={urls.qrSocial}
            />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default EventUrlsCard;
